/**
 * ListBuilderMenu — dropdown „Kreator ▾" nad listą (UX 2026-06-02).
 *
 * Konsoliduje 2 wcześniejsze przyciski (Kreator Listy + Kreator Formularza)
 * w jeden przycisk z dropdownem. Domyślnie nawiguje do widoków modułu systemowego:
 *  - Kreator Listy   → /system-modules/:slug/kreator-listy[?layout=<widok wybrany nad listą>]
 *  - Kreator Formularza → /system-modules/:slug/builder
 *
 * Dla kartotek modułów (ModuleRecordsPage) trasy różnią się (`/modules/:id|:slug/...`),
 * więc można je nadpisać przez `listPath` / `formPath` — wtedy `slug` nie jest używany,
 * a wybrany widok wskazuje `activeLayoutTableId`.
 * Dzięki temu systemowe i custom kartoteki korzystają z JEDNEGO komponentu paska.
 *
 * Samodzielny: własny stan otwarcia + zamykanie na Esc / klik poza.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LayoutList, Settings as SettingsIcon, Wrench } from 'lucide-react';

import { getActiveListLayout } from './active-list-layout';

interface ListBuilderMenuProps {
  /** Slug modułu systemowego (np. `companies`, `contractors`) — użyty do domyślnych tras. */
  slug?: string;
  /** Nadpisanie trasy Kreatora Listy (kartoteki custom). Gdy brak — trasa systemowa ze `slug`. */
  listPath?: string;
  /** Nadpisanie trasy Kreatora Formularza (kartoteki custom). Gdy brak — trasa systemowa ze `slug`. */
  formPath?: string;
  /** Przy `listPath`: `tableId` listy (np. `module-<id>`), której wybrany widok niesie kreator. */
  activeLayoutTableId?: string;
  testIdPrefix?: string;
  /** Nadpisanie klas przycisku-triggera (np. `h-10 !rounded-full` w nagłówku kartoteki). */
  triggerClassName?: string;
}

export function ListBuilderMenu({
  slug,
  listPath,
  formPath,
  activeLayoutTableId,
  testIdPrefix = 'list',
  triggerClassName,
}: ListBuilderMenuProps): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  // Kreator otwiera widok wybrany teraz w przełączniku nad listą (`?layout=<id>`), nie
  // domyślny. Czytane w chwili kliknięcia — tabela zgłasza wybór do rejestru
  // `active-list-layout` pod swoim `tableId` (kartoteka systemowa: slug; modułu: `module-<id>`).
  const activeKey = listPath ? activeLayoutTableId : slug;
  const activeLayoutId = activeKey ? getActiveListLayout(activeKey) : null;
  const listTarget = `${listPath ?? `/system-modules/${slug}/kreator-listy`}${
    activeLayoutId ? `?layout=${encodeURIComponent(activeLayoutId)}` : ''
  }`;
  const formTarget = formPath ?? `/system-modules/${slug}/builder`;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('common.builderAria')}
        title={t('common.builderAria')}
        className={`inline-flex h-11 items-center gap-2 rounded-full border border-border-secondary bg-bg-elevated pl-4 pr-5 text-sm text-text-primary shadow-[0_1px_1px_rgba(23,23,33,0.06)] transition-colors hover:bg-bg-tertiary ${triggerClassName ?? ''}`}
        data-testid={`${testIdPrefix}-builder-btn`}
      >
        <Wrench size={16} aria-hidden="true" />
        {t('common.builder')}
        <ChevronDown size={14} aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 min-w-[200px] rounded-lg border border-border-secondary bg-bg-elevated py-1 shadow-lg"
          data-testid={`${testIdPrefix}-builder-menu`}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => go(listTarget)}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-text-primary transition-colors hover:bg-bg-secondary"
            data-testid={`${testIdPrefix}-builder-list`}
          >
            <LayoutList size={14} aria-hidden="true" />
            {t('common.listBuilder')}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => go(formTarget)}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-text-primary transition-colors hover:bg-bg-secondary"
            data-testid={`${testIdPrefix}-builder-form`}
          >
            <SettingsIcon size={14} aria-hidden="true" />
            {t('common.formBuilder')}
          </button>
        </div>
      )}
    </div>
  );
}
